export function isBuilderGuideEnabled(value: string | undefined) {
  return value === "true";
}

export const builderGuideEnabled = isBuilderGuideEnabled(import.meta.env.VITE_APP_BUILDER_GUIDE_ENABLED);

export const buildSteps = [
  { slug: "start", shortLabel: "Start", title: "Start with a useful problem" },
  { slug: "choose-tool", shortLabel: "Choose a tool", title: "Choose your coding agent" },
  { slug: "add-guides", shortLabel: "Add guides", title: "Add the project guides" },
  { slug: "build", shortLabel: "Build", title: "Build with an agent" },
  { slug: "test", shortLabel: "Test & iterate", title: "Run, test, and iterate" },
  { slug: "prepare", shortLabel: "Prepare upload", title: "Prepare the project for Cheesy Apps" },
  { slug: "submit", shortLabel: "Submit", title: "Submit and publish" },
] as const;

export type BuildStepSlug = (typeof buildSteps)[number]["slug"];
export type BuildTool = "codex" | "claude";

export function parseBuildStep(value: string | null): BuildStepSlug {
  return buildSteps.some((step) => step.slug === value) ? (value as BuildStepSlug) : "start";
}

export function parseBuildTool(value: string | null): BuildTool {
  return value === "claude" ? "claude" : "codex";
}

export function stepIndex(slug: BuildStepSlug) {
  return buildSteps.findIndex((step) => step.slug === slug);
}

export function adjacentStep(slug: BuildStepSlug, offset: -1 | 1): BuildStepSlug | null {
  return buildSteps[stepIndex(slug) + offset]?.slug ?? null;
}

export const AGENTS_ADAPTER = `# Cheesy Apps project instructions

Before planning or implementing any change, read these files completely:

- APP_DESIGN_CONTEXT_V2.md
- MAGIC_PROMPT_V2.md

Use the completed product brief in APP_DESIGN_CONTEXT_V2.md as the authority for product intent, users, workflows, and experience. Use MAGIC_PROMPT_V2.md as the authority for the technical baseline, project structure, quality checks, and delivery requirements.

Read references/LAUNCHER_INTEGRATION.md for host themes, roles, and mobile behavior. Read references/API_INTEGRATION.md before adding an API, and references/DELIVERY_AND_MAINTENANCE.md before release. Read convex/_generated/ai/guidelines.md before changing Convex code.

Preserve any project-specific instructions already present in this file. If instructions conflict, stop and ask the project owner which rule should govern.`;

export const CLAUDE_ADAPTER = `# Cheesy Apps project instructions

@APP_DESIGN_CONTEXT_V2.md
@MAGIC_PROMPT_V2.md

Read both imported guides completely before planning or implementing any change.

Use the completed product brief in APP_DESIGN_CONTEXT_V2.md as the authority for product intent, users, workflows, and experience. Use MAGIC_PROMPT_V2.md as the authority for the technical baseline, project structure, quality checks, and delivery requirements.

Read references/LAUNCHER_INTEGRATION.md for host themes, roles, and mobile behavior. Read references/API_INTEGRATION.md before adding an API, and references/DELIVERY_AND_MAINTENANCE.md before release. Read convex/_generated/ai/guidelines.md before changing Convex code.

Preserve any project-specific instructions already present in this file. If instructions conflict, stop and ask the project owner which rule should govern.`;

export const KIT_FILENAME = "cheesy-apps-development-kit-v2-2026.10.zip";

export const toolSetup = {
  codex: {
    downloadUrl: "https://openai.com/codex/",
    setupUrl: "https://learn.chatgpt.com/docs/agent-configuration/agents-md",
    steps: [
      "Download Codex and sign in with your ChatGPT account.",
      "Add your app folder as a project so Codex can see and change its files.",
      "Codex follows the AGENTS.md file included in the App Development Kit.",
    ],
  },
  claude: {
    downloadUrl: "https://claude.com/download",
    setupUrl: "https://code.claude.com/docs/en/desktop-quickstart",
    instructionsUrl: "https://code.claude.com/docs/en/memory",
    steps: [
      "Download Claude, sign in, and open the Code tab.",
      "Choose Local, then select your app folder.",
      "Claude Code follows the CLAUDE.md file included in the App Development Kit.",
    ],
  },
} as const;

export const KIT_PATHS = [
  "APP_DESIGN_CONTEXT_V2.md",
  "MAGIC_PROMPT_V2.md",
  "AGENTS.md",
  "CLAUDE.md",
  "references/STARTER_PRESET_SPEC.md",
  "references/SSO_BRIDGE_SPEC.md",
  "references/CHANGE_HIGHLIGHTS.md",
  "references/API_INTEGRATION.md",
  "references/LAUNCHER_INTEGRATION.md",
  "references/DELIVERY_AND_MAINTENANCE.md",
  "examples/api/http.ts.example",
  "examples/api/apiProfiles.ts.example",
  "examples/api/nginx.location.conf.example",
  "convex/lib/cheesyApi.ts",
  "convex/lib/cheesyApiContracts.ts",
  "src/lib/cheesy-launcher.ts",
] as const;

export const agentPrompts = [
  {
    label: "Explore and plan",
    text: "Read the Cheesy Apps instruction files in this project completely. Look through the project without changing anything yet. Ask me any questions you need, then give me a short, plain-language plan for the app's main workflow and how you will test it.",
  },
  {
    label: "Build one complete workflow",
    text: "Build the smallest complete version of the main workflow in the product brief. Follow all Cheesy Apps project instructions, use the included shared components, run the app, and test the workflow before showing me the result.",
  },
  {
    label: "Review the experience",
    text: "Run the app and visually review the main workflow on phone and desktop in light and dark themes. Check loading, empty, error, keyboard, focus, and narrow-screen behavior. Tell me what you found before making broad design changes.",
  },
  {
    label: "Make a focused revision",
    text: "Change only the following behavior: [describe the expected result]. Reproduce the current behavior first, make the smallest reliable correction, and confirm the expected result without changing unrelated parts of the app.",
  },
  {
    label: "Final verification",
    text: "Review every change you made and run the project's complete verification process. Confirm the production build, main user workflow, accessibility checks, container health, and Cheesy Apps upload requirements. Fix issues you can safely fix, then clearly tell me what passed and what still needs a person to check.",
  },
] as const;

export const createProjectPrompt = `Create a new Cheesy App named [APP NAME]. Use the official Cheesy Apps creator below and handle the setup for me:

bunx --package https://cheesyapps.com/releases/team254-create-cheesy-app-2026.10.2.tgz create-cheesy-app "[APP NAME]"

Create it inside this folder. Ask me before choosing a Cheesy Apps app ID or Convex team, and pause if I need to sign in or approve an installation. When setup is finished, tell me where the app was created and what I should do next.`;

export const startTestingPrompt = `Set up and run this Cheesy App for me. If this is a new or freshly downloaded project, run its bootstrap process first. Otherwise start its normal development server. Keep it running, give me the local browser link, and tell me when it is ready to test.`;

export const verifyProjectPrompt = `Run this project's complete verification process. Fix problems that are clearly caused by our work, but do not remove or weaken tests to make them pass. Then tell me in plain language what passed, what you changed, and anything I still need to check myself.`;

export const prepareProjectPrompt = `Prepare this project for upload to Cheesy Apps. Follow the Cheesy Apps guides and check every item required by cheesy-app.json, the Dockerfile, /health, the saved Bun lockfile, Convex configuration, secret scanning, and the complete verification process. Fix safe project issues, do not publish or submit anything yet, and give me a plain-language list of what is ready and anything that still needs my help.`;
